import type { AdminGiftObligationsReport } from "@/types/admin-gift-obligations";

/**
 * Mock: remaining gifted membership.
 *
 * Deliberately shaped so the screen's HARD cases are visible in demo mode rather
 * than only in production:
 *
 *   - a stacked account (three gifts) — the "stacked" the owner asked about, and
 *     a normal customer, so the screen must render it without risk language;
 *   - an account whose furthest gift has NO dates, which must show as "not known
 *     yet" and never as an invented date;
 *   - `undatedGifts` larger than anything in `byEndMonth`, because sold-but-
 *     unredeemed codes are the largest bucket at launch and are invisible to any
 *     month breakdown. A screen built only against dated data looks complete and
 *     is not;
 *   - one `activePastEndDate`, the visible symptom of the gift clock worker
 *     shipping switched off;
 *   - a `settled` bucket with zero days, so a reviewer can see that refunded
 *     money is reported for reconciliation but owes nothing.
 *
 * The numbers are internally consistent: 4 + 3 + 2 = 9 outstanding gifts, and
 * the per-category values sum to the headline.
 */
export const MOCK_GIFT_OBLIGATIONS: AdminGiftObligationsReport = {
  totals: {
    outstandingGifts: 9,
    // 4 x 365 (unredeemed) + 3 x 365 (pending) + 183 + 40 (two part-used)
    remainingServiceDays: 2778,
    // 4 unredeemed codes (nobody has redeemed them, so no dates) + account 5510's
    // two held gifts.
    undatedGifts: 6,
    // Only REDEEMED gifts belong to an account — an unredeemed code has no
    // recipient yet. So the 5 redeemed gifts sit on 2 accounts, both stacked.
    accountsWithOutstandingGifts: 2,
    accountsWithStackedGifts: 2,
    activePastEndDate: 1,
  },
  remainingValue: [
    // 4 x 34800 + 3 x 34800 + 17448 + 3814 = 264_862
    { currency: "usd", remainingCents: 264862, gifts: 9 },
  ],
  categories: [
    {
      category: "sold_unredeemed",
      label: "Sold but unredeemed",
      gifts: 4,
      remainingServiceDays: 1460,
      remainingValue: [{ currency: "usd", remainingCents: 139200, gifts: 4 }],
    },
    {
      category: "pending_activation",
      label: "Redeemed and pending activation",
      gifts: 3,
      remainingServiceDays: 1095,
      remainingValue: [{ currency: "usd", remainingCents: 104400, gifts: 3 }],
    },
    {
      category: "active_remaining",
      label: "Active gifted membership remaining",
      gifts: 2,
      remainingServiceDays: 223,
      remainingValue: [{ currency: "usd", remainingCents: 21262, gifts: 2 }],
    },
    {
      category: "settled",
      label: "Refunded, disputed, charged back, or cancelled",
      gifts: 2,
      remainingServiceDays: 0,
      remainingValue: [{ currency: "usd", remainingCents: 0, gifts: 2 }],
    },
  ],
  byEndMonth: [
    { month: "2027-02", gifts: 1 },
    { month: "2027-09", gifts: 1 },
    { month: "2028-03", gifts: 1 },
  ],
  stackedAccounts: [
    {
      userId: 4021,
      outstandingGifts: 3,
      coverageEndsAt: "2029-03-14T12:00:00+00:00",
      hasUndatedGift: false,
    },
    {
      // Furthest gift has no dates — the screen must say so, not guess.
      userId: 5510,
      outstandingGifts: 2,
      coverageEndsAt: null,
      hasUndatedGift: true,
    },
  ],
};
